import type { KeyboardEvent, Ref } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { NavItem, NavOnglet } from '../../lib/navigation'
import { idOngletDePage, ongletCourant } from '../../lib/ongletsDePage'
import { useLienClient } from '../../contexts/ClientAfficheContext'

/**
 * Spec 161 (D-161-07, FR-041) — barre d'onglets d'une entrée de menu qui réunit des pages (« Licences et économies »,
 * « Analyses »…). La plateforme ne sait pas quelles pages : elle lit les onglets que la solution DÉCLARE dans l'entrée.
 *
 * Chaque onglet est une page existante, à sa propre adresse : aucune adresse ne change, aucune redirection n'est
 * nécessaire. L'onglet est un LIEN qui emporte le client affiché (`useLienClient`, spec 160). Le modèle est celui des
 * onglets du portail (règle de design, spec 160) : `tablist` nommée par l'entrée, onglet ouvert seul atteignable par
 * Tab, flèches, Début et Fin ; le panneau (`idPanneau`) est le contenu de la page, posé par la coquille.
 */
export function OngletsDePage({ entree, idPanneau, dansLaPage = false, refBarre }: {
  entree: NavItem
  idPanneau: string
  /** Posée par la page sous son titre (`OngletsDeLEntree`) : sans marge propre, la page a déjà la sienne. */
  dansLaPage?: boolean
  /** La barre elle-même, pour savoir si elle a le focus quand elle se démonte (`BarreOngletsSuivie`). */
  refBarre?: Ref<HTMLDivElement>
}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const lien = useLienClient()
  const onglets = entree.onglets ?? []
  const courant = ongletCourant(entree, pathname)
  const idOnglet = (o: NavOnglet) => idOngletDePage(entree, o)

  const changerAuClavier = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = Math.max(0, onglets.findIndex(o => o.id === courant?.id))
    const n = onglets.length
    const cible = e.key === 'ArrowRight' ? (i + 1) % n
      : e.key === 'ArrowLeft' ? (i - 1 + n) % n
      : e.key === 'Home' ? 0
      : e.key === 'End' ? n - 1
      : null
    if (cible === null) return
    e.preventDefault()
    const onglet = onglets[cible]!
    navigate(lien(onglet.path))
    // Le focus suit l'onglet ouvert, une fois la page rendue.
    requestAnimationFrame(() => document.getElementById(idOnglet(onglet))?.focus())
  }

  return (
    <div
      ref={refBarre}
      role="tablist"
      aria-label={entree.label}
      onKeyDown={changerAuClavier}
      style={{
        display: 'flex',
        gap: 4,
        padding: dansLaPage ? 0 : '0 24px',
        marginBottom: dansLaPage ? 20 : 0,
        borderBottom: '1px solid hsl(var(--border))',
        background: dansLaPage ? 'transparent' : 'hsl(var(--background))',
        flexShrink: 0,
        overflowX: 'auto',
      }}
    >
      {onglets.map(o => {
        const actif = o.id === courant?.id
        return (
          <Link
            key={o.id}
            id={idOnglet(o)}
            to={lien(o.path)}
            role="tab"
            aria-selected={actif}
            aria-controls={idPanneau}
            tabIndex={actif ? 0 : -1}
            style={{
              padding: '10px 14px',
              minHeight: 24,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              fontWeight: actif ? 600 : 'normal',
              color: actif ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
              textDecoration: 'none',
              borderBottom: `2px solid ${actif ? 'hsl(var(--accent))' : 'transparent'}`,
              marginBottom: -1,
              whiteSpace: 'nowrap',
              transition: 'color 0.12s, border-color 0.12s',
            }}
          >
            {o.label}
          </Link>
        )
      })}
    </div>
  )
}
